import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import React, {useCallback, useEffect} from 'react';
import * as _debounce from 'lodash/debounce';
import _ from 'lodash';
import {hasPermission, Permissions} from '../../../../../store/reducers/user';
import moment from 'moment/moment';
import {additionalData} from '../../../../../store/reducers/additionalData';
import * as _find from 'lodash/find';
import div from '@mui/material/Paper';
import clsx from 'clsx';
import ArrowRight from '@mui/icons-material/ArrowRight';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import config from '../../../../../app/config';
import InformationBox from '../../../../../components/other/informationBox';
import EditorMarkdownComponent from '../../../../../components/editor/editorMarkdown';
import PropTypes from 'prop-types';
import {emitChangedWait, obsTypes, riskLevels} from '../obsUtils';
import useDict from '../../../../../hooks/useDict';
import Chip from '@mui/material/Chip';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {observationConfig} from '../../../../../store/reducers/externalConfig';
import {SepsisPathwayStatus} from '../../../sepsis/sepsisUtil';
import {obsStyles} from '../obsStyle';
import {useTheme} from '@mui/styles';
import {getAdmissionStartDate} from '../../../../../util/patientUtil';

const obsNeuroNextItems = [
    {
        id: '15',
        value: 15,
        scoreMin: 0,
        description: '00:15',
        default: false,
    },
    {
        id: '30',
        value: 30,
        scoreMin: 0,
        description: '00:30',
        default: false,
    },
    {
        id: '60',
        value: 60,
        scoreMin: 0,
        description: '01:00',
        default: true,
    },
    {
        id: '120',
        value: 120,
        scoreMin: 0,
        description: '02:00',
        default: false,
    },
    {
        id: '180',
        value: 180,
        scoreMin: 0,
        description: '03:00',
        default: false,
    },
];

const nursingOption = [
    {
        id: 1,
        tooltip: 'Nurse/clinician concern that patient needs increased monitoring despite low PEWS',
        riskLevel: riskLevels.low,
        availableAt: [riskLevels.continue, riskLevels.low],
        color: 'obsColorRiskLow',
    },
    {
        id: 2,
        tooltip: 'Nurse/clinician concern that patient needs a medical review irrespective of PEWS',
        riskLevel: riskLevels.medium,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium],
        color: 'obsColorRiskMedium',
    },
    {
        id: 3,
        tooltip: 'Nurse/clinician concern that patient needs a ‘Rapid Review’ irrespective of PEWS',
        riskLevel: riskLevels.high,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium, riskLevels.high],
        color: 'obsColorRiskHigh',
    },
    {
        id: 4,
        tooltip: 'Nurse/clinician concern that patient needs emergency review for life-threatening situation',
        riskLevel: riskLevels.emergency,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium, riskLevels.high, riskLevels.emergency],
        color: 'obsColorRiskEmergency',
    },
];

const familyOption = [
    {
        id: 1,
        tooltip: 'Carer uses words that suggests the child needs increased monitoring or intervention despite the low PEWS',
        riskLevel: riskLevels.low,
        availableAt: [riskLevels.continue, riskLevels.low],
        color: 'obsColorRiskLow',
    },
    {
        id: 2,
        tooltip: 'Carer uses words that suggests the child needs a clinical review irrespective of PEWS',
        riskLevel: riskLevels.medium,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium],
        color: 'obsColorRiskMedium',
    },
    {
        id: 3,
        tooltip: 'Carer uses words that suggests the child needs a ‘Rapid Review’ irrespective of PEWS',
        riskLevel: riskLevels.high,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium, riskLevels.high],
        color: 'obsColorRiskHigh',
    },
    {
        id: 4,
        tooltip: 'Carer uses words that suggests the child has collapsed or significantly deteriorated',
        riskLevel: riskLevels.emergency,
        availableAt: [riskLevels.continue, riskLevels.low, riskLevels.medium, riskLevels.high, riskLevels.emergency],
        color: 'obsColorRiskEmergency',
    },
];

function ObsScore(props) {
    const dispatch = useDispatch();
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears, startedDate, patient} = props;
    const theme = useTheme();

    const newRiskAssessment = config?.observations?.newRiskAssessment;
    const obsCfg = useSelector(observationConfig);

    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [nextObs, setNextObs] = React.useState(props.nextObs || null);
    const [nextObsDate, setNextObsDate] = React.useState(props.nextObsDate);

    const [obsNextItems, setObsNextItems] = React.useState([]);
    const [clinicalResponse, setClinicalResponse] = React.useState(null);

    const [obsClinicalResponses, setObsClinicalResponses] = React.useState([]);
    const [obsAllNextItems, setObsAllNextItems] = React.useState([]);

    const [option1, setOption1] = React.useState(null);
    const [option1Reason, setOption1Reason] = React.useState(null);
    const [option2, setOption2] = React.useState(null);
    const [option3, setOption3] = React.useState(null);
    const [option3Reason, setOption3Reason] = React.useState(null);
    const [option4, setOption4] = React.useState(null);
    const [option4Reason, setOption4Reason] = React.useState(null);

    // Sepsis
    const patientAddData = useSelector(additionalData);
    const incompleteSepsisPath = patientAddData && patientAddData.other && patientAddData.other.sepsisPathways
        ? _find(patientAddData.other.sepsisPathways, ['statusObj', SepsisPathwayStatus.INCOMPLETE])
        : null;

    // Dict
    const [clinicalResponsesDict] = useDict('ClinicalResponses');

    useEffect(() => setOption1(props.extraOption1), [props.extraOption1]);
    useEffect(() => setOption1Reason(props.extraOption1Reason), [props.extraOption1Reason]);
    useEffect(() => setOption2(props.extraOption2), [props.extraOption2]);
    useEffect(() => setOption3(props.extraOption3), [props.extraOption3]);
    useEffect(() => setOption3Reason(props.extraOption3Reason), [props.extraOption3Reason]);
    useEffect(() => setOption4(props.extraOption4), [props.extraOption4]);
    useEffect(() => setOption4Reason(props.extraOption4Reason), [props.extraOption4Reason]);

    useEffect(() => {
        let obsNextTimeItems = [];

        obsNextTimeItems.push({
            id: '5',
            value: 5,
            scoreMin: 0,
            description: '00:05',
        });

        obsNextTimeItems.push({
            id: '10',
            value: 10,
            scoreMin: 0,
            description: '00:10',
        });

        obsNextTimeItems.push({
            id: '15',
            value: 15,
            scoreMin: 0,
            description: '00:15',
        });

        obsNextTimeItems.push({
            id: '20',
            value: 20,
            scoreMin: 0,
            description: '00:20',
        });

        obsNextTimeItems.push({
            id: '25',
            value: 25,
            scoreMin: 0,
            description: '00:25',
        });

        obsNextTimeItems.push({
            id: '30',
            value: 30,
            scoreMin: 0,
            description: '00:30',
        });

        obsNextTimeItems.push({
            id: '35',
            value: 35,
            scoreMin: 0,
            description: '00:35',
        });

        obsNextTimeItems.push({
            id: '40',
            value: 40,
            scoreMin: 0,
            description: '00:40',
        });

        obsNextTimeItems.push({
            id: '45',
            value: 45,
            scoreMin: 0,
            description: '00:45',
        });

        obsNextTimeItems.push({
            id: '50',
            value: 50,
            scoreMin: 0,
            description: '00:50',
        });

        obsNextTimeItems.push({
            id: '55',
            value: 55,
            scoreMin: 0,
            description: '00:55',
        });

        obsNextTimeItems.push({
            id: '60',
            value: 60,
            scoreMin: 0,
            description: '01:00',
            default: true,
        });

        if (props.overHour) {
            obsNextTimeItems.push({
                id: '75',
                value: 75,
                scoreMin: 0,
                scoreMax: 4,
                description: '01:15',
            });

            obsNextTimeItems.push({
                id: '90',
                value: 90,
                scoreMin: 0,
                scoreMax: 4,
                description: '01:30',
            });

            obsNextTimeItems.push({
                id: '105',
                value: 105,
                scoreMin: 0,
                scoreMax: 4,
                description: '01:45',
            });

            obsNextTimeItems.push({
                id: '120',
                value: 120,
                scoreMin: 0,
                scoreMax: 4,
                description: '02:00',
            });

            obsNextTimeItems.push({
                id: '135',
                value: 135,
                scoreMin: 0,
                scoreMax: 4,
                description: '02:15',
            });

            obsNextTimeItems.push({
                id: '150',
                value: 150,
                scoreMin: 0,
                scoreMax: 4,
                description: '02:30',
            });

            obsNextTimeItems.push({
                id: '165',
                value: 165,
                scoreMin: 0,
                scoreMax: 4,
                description: '02:45',
            });

            obsNextTimeItems.push({
                id: '180',
                value: 180,
                scoreMin: 0,
                scoreMax: 4,
                description: '03:00',
            });

            obsNextTimeItems.push({
                id: '240',
                value: 240,
                scoreMin: 0,
                scoreMax: 4,
                description: '04:00',
            });

            if (type !== obsTypes.pews) {
                obsNextTimeItems.push({
                    id: '300',
                    value: 300,
                    scoreMin: 0,
                    scoreMax: 4,
                    description: '05:00',
                });

                obsNextTimeItems.push({
                    id: '360',
                    value: 360,
                    scoreMin: 0,
                    scoreMax: 4,
                    description: '06:00',
                });

                obsNextTimeItems.push({
                    id: '420',
                    value: 420,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '07:00',
                });

                obsNextTimeItems.push({
                    id: '480',
                    value: 480,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '08:00',
                });

                obsNextTimeItems.push({
                    id: '540',
                    value: 540,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '09:00',
                });

                obsNextTimeItems.push({
                    id: '600',
                    value: 600,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '10:00',
                });

                obsNextTimeItems.push({
                    id: '660',
                    value: 660,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '11:00',
                });

                obsNextTimeItems.push({
                    id: '720',
                    value: 720,
                    scoreMin: 0,
                    scoreMax: 0,
                    description: '12:00',
                });
            }
        }
        setObsAllNextItems(obsNextTimeItems);
        setObsClinicalResponses(_.filter(clinicalResponsesDict, {type}));
        if (type === obsTypes.neuro) {
            setObsAllNextItems([
                {
                    id: '60',
                    value: 60,
                    scoreMin: 0,
                    description: '01:00',
                    default: true,
                },
            ]);
        }
    }, [type, ageRangeYears, clinicalResponsesDict]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    nextObs,
                    nextObsDate,
                    clinicalResponse,
                    extraOption1: option1,
                    extraOption1Reason: option1Reason,
                    extraOption2: option2,
                    extraOption3: option3,
                    extraOption3Reason: option3Reason,
                    extraOption4: option4,
                    extraOption4Reason: option4Reason,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        acceptDate,
        nextObs,
        nextObsDate,
        clinicalResponse,
        option1,
        option1Reason,
        option2,
        option3,
        option3Reason,
        option4,
        option4Reason,
    ]);

    useEffect(() => {
        console.log('===');
        console.log('riskLevel', props.riskLevel);
        console.log('TotalScore', props.totalScore);
        console.log('===');
        let response = _.find(obsClinicalResponses, (item) => {
            return (
                item.riskLevel === props.riskLevel &&
                Number(item.scoreMin) <= props.totalScore &&
                (!('scoreMax' in item) || Number(item.scoreMax) >= props.totalScore)
            );
        });
        setClinicalResponse(response);

        //reset possibilities according permissions

        let U_can_reduce = dispatch(hasPermission(Permissions.AGYLE_CAN_REDUCE_OBSERVATON_FREQUENCY));
        let U_can_increase = dispatch(hasPermission(Permissions.AGYLE_CAN_INCREASE_OBSERVATION_FREQUENCY));

        let items = obsAllNextItems.filter((item) => {
            if (props.type === obsTypes.pews && props.consciousness?.consciousnessLevel?.id !== 'alert') {
                return (
                    item.scoreMin <= props.totalScore &&
                    (!item.hasOwnProperty('scoreMax') ||
                        item.scoreMax >= props.totalScore) &&
                    item.value <= 30
                );
            }
            // response.value - default selected
            if (response) {
                if (item.scoreMin <= props.totalScore && (!item.hasOwnProperty('scoreMax') || item.scoreMax >= props.totalScore)) {
                    return (
                        (item.value < Number(response.value) && U_can_increase) ||
                        (item.value > Number(response.value) && U_can_reduce) ||
                        item.value === Number(response.value)
                    );
                }
            }
            return false;
        });
        setObsNextItems(items);

        let defItem = null;
        if (response) {
            defItem = _.find(items, (item) => {
                return item.id === response.value;
            });
        }
        if (!defItem) {
            defItem = _.find(items, (item) => {
                return item.default;
            });
        }
        if (!defItem) {
            defItem = _.last(items);
        }
        updateNextObs(props.allAccepted ? defItem : null);

        // console.log("ObsScore.props.riskLevel", props.riskLevel);
        // console.log("ObsScore.obsClinicalResponses", obsClinicalResponses);
        // console.log("ObsScore.response", response);
        // console.log("ObsScore.obsAllNextItems", obsAllNextItems);
        // console.log("ObsScore.nextItems", items);
        // console.log("ObsScore.defItem", defItem);
    }, [
        props.totalScore,
        props.anyAccepted,
        props.allAccepted,
        props.riskLevel,
        type,
        ageRangeYears,
        obsClinicalResponses,
        obsAllNextItems,
    ]);

    useEffect(() => {
        let newDisabled = false;
        if (type === obsTypes.news && !props.anyAccepted) {
            newDisabled = true;
        }
        if (props.allAccepted && type !== obsTypes.neuro) {
            if (!nextObs) {
                newDisabled = true;
            }
            if (option1 == null) {
                newDisabled = true;
            }
            if (option2 == null) {
                newDisabled = true;
            }
            if (option3 == null) {
                if (dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_YN_SEPSIS))) {
                    newDisabled = true;
                }
            }
            if ((option1 === true || option2 === true) && option3 === false && dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_YN_SEPSIS))
                && (option3Reason == null || option3Reason === '')) {

                newDisabled = !(type === obsTypes.pews &&
                    props.painScore.painScore >= 5 &&
                    props.totalScore === 0);
            }

            if (newRiskAssessment && type === obsTypes.pews) {
                if ((option1 && _.isNull(option1Reason)) || (option4 && _.isNull(option4Reason))) newDisabled = true;
            }
        }
        if (props.setAcceptDisabled) {
            props.setAcceptDisabled(newDisabled);
        }
    }, [
        props.totalScore,
        type,
        props.anyAccepted,
        nextObs,
        props.allAccepted,
        option1,
        option1Reason,
        option2,
        option3,
        option3Reason,
        option4,
        option4Reason,
    ]);

    const updateNextObs = (value) => {
        setNextObs(value);
        if (value) {
            if (config?.observations?.retrospective && obsCfg?.retrospective) {
                setNextObsDate(moment(startedDate).add(value.value, 'minutes'));
            } else {
                setNextObsDate(moment().add(value.value, 'minutes'));
            }
        } else {
            setNextObsDate(null);
        }
    };

    const getColor = (score) => {
        let obsColor;
        const themeColors = theme?.palette?.observation;

        const age = moment.duration(getAdmissionStartDate(patient?.admission).diff(moment(patient?.patient?.dob))).asYears();
        const isAdult = age >= 18;

        if (isAdult) {
            if (score >= 9) obsColor = themeColors?.red;
            else if (score < 9 && score >= 7) obsColor = themeColors?.amber;
            else if (score < 7 && score >= 5) obsColor = themeColors?.yellow;
            else obsColor = themeColors?.white;
        } else {
            if (score >= 13) obsColor = themeColors?.red;
            else if (score < 13 && score >= 9) obsColor = themeColors?.pink;
            else if (score < 9 && score >= 5) obsColor = themeColors?.amber;
            else if (score < 5 && score >= 1) obsColor = themeColors?.yellow;
            else obsColor = themeColors?.white;
        }

        return obsColor;
    };

    const getScoreBox = (score) => {
        const color = getColor(score);
        return (
            <div
                className={clsx(classes.scoreBadge, type === obsTypes.neuro && classes.obsColorUnknown)}
                style={{
                    backgroundColor: type === obsTypes.neuro ? theme?.palette?.primary?.light : color,
                    color: type === obsTypes.neuro ? theme?.palette?.primary?.contrastText : theme?.palette?.observation?.text,
                }}
            >
                {score !== null && score >= 0 && score}
                {score === null && <span>-</span>}
            </div>
        );
    };

    const showScore = useCallback(() => {
        const checkEscalation = props.type !== obsTypes.neuro ? props.prevTotalScore > props.totalScore ? 1 : props.prevTotalScore < props.totalScore ? -1 : 0 : props.prevTotalScore > props.totalScore ? -1 : props.prevTotalScore < props.totalScore ? 1 : 0;

        if (props.type === obsTypes.neuro && props.isNT) {
            return (
                <>
                    <div
                        className={clsx(
                            classes.totalScoreLabel,
                        )}>
                        {t('No Escalation')}
                    </div>
                </>
            );
        }

        // eslint-disable-next-line default-case
        switch (checkEscalation) {
            case 1:
            case -1: {
                return (
                    <>
                        <div
                            className={clsx(
                                classes.totalScoreLabel,
                            )}
                        >
                            {t(checkEscalation === 1 ? 'Improvement' : 'Escalation')}
                        </div>

                        <div className={classes.totalScoreEscalation}>
                            <div className={classes.totalScoreRootInner}>
                                {getScoreBox(props.prevTotalScore)}
                            </div>

                            <div className={classes.totalScoreEscalationArrow}>
                                <div className={classes.totalScoreEscalationArrowIcon}
                                >
                                    <ArrowRight/>
                                </div>
                            </div>

                            <div className={classes.totalScoreRootInner}>
                                {getScoreBox(props.totalScore)}
                            </div>
                        </div>

                        {props.type === obsTypes.neuro && checkEscalation === -1 &&
                            <Grid item xs={12}>
                                <div
                                    style={{marginTop: 10}}
                                    className={classes.consciousnessWarning}
                                >
                                    {t('Patients GCS has decreased since last taken, consider escalation to a senior clinician')}
                                </div>
                            </Grid>
                        }
                    </>
                );
            }

            case 0: {
                return (
                    <>
                        <div className={clsx(classes.totalScoreLabel)}>
                            {t('No Escalation')}
                        </div>
                    </>
                );
            }
        }
    }, [
        props.prevTotalScore,
        props.totalScore,
        props.riskLevel,
        props.prevRiskLevel,
        props.isNT,
    ]);

    const showTotalScore = () => {
        if (props.type === obsTypes.neuro && props.isNT) {
            return 'NT';
        }
        return props.totalScore;
    };

    const option3ReasonVisible = () => {
        if (type === obsTypes.pews && props.totalScore === 0 && option3 === false && props.painScore.painScore >= 5)
            return false;

        return (
            (option1 === true || option2 === true) && option3 === false
        );
    };

    const handleSelectOption = (option, val) => {
        if (option === '1') setOption1Reason(option1Reason?.id === val?.id ? null : val);
        if (option === '4') setOption4Reason(option4Reason?.id === val?.id ? null : val);
    };

    const getOption1Label = () => {
        if (type === obsTypes.news) {
            return 'ARE YOU STILL WORRIED? or single parameter of 3';
        } else {
            return newRiskAssessment ? 'Nursing Instinct: Are you still worried'
                : 'Nursing Instinct: Are you still worried / parents are concerned or single parameter of 4';
        }
    };

    const getAvpuLabel = () => {
        if (type === obsTypes.news) {
            return 'NEWS SCORE equal to 5+ or the patient looks sick';
        } else {
            return newRiskAssessment ? '"Alert" not on AVPU scale'
                : 'PEWS score 5+ or not "Alert" on AVPU scale';
        }
    };

    const getLabelColor = () => {
        if (newRiskAssessment && type !== obsTypes.neuro) {
            switch (props.riskLevel) {
                case riskLevels.low:
                    return classes.obsColorRiskLow;
                case riskLevels.medium:
                    return classes.obsColorRiskMedium;
                case riskLevels.high:
                    return classes.obsColorRiskHigh;
                case riskLevels.emergency:
                    return classes.obsColorRiskEmergency;
                case riskLevels.continue:
                default:
                    return classes.obsColorRiskContinue;
            }
        } else {
            switch (props.riskLevel) {
                case riskLevels.low:
                    return classes.obsColorLow;
                case riskLevels.mediumLow:
                    return classes.obsColorMediumLow;
                case riskLevels.medium:
                    return classes.obsColorMedium;
                case riskLevels.high:
                    return classes.obsColorHigh;
                default:
                    return '';
            }
        }
    };

    const getSwitchVal = (val) => {
        if (_.isBoolean(val)) return val;
        return null;
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={0}>
                    <Grid item xs={12}>
                        <div className={classes.obsLabel}>
                            {type === obsTypes.news ? t('NEWS') : type === obsTypes.pews ? t('PEWS Score') : t('Glasgow Coma Scale')}
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <div className={classes.totalScoreRoot} style={{display: 'flex', alignItems: 'center', gap: 10}}>
                            <div className={classes.totalScoreLabel}>
                                {t('Score')}
                            </div>

                            <div className={classes.totalScoreValue}>
                                {showTotalScore()}
                            </div>
                            {showScore()}
                        </div>
                    </Grid>

                    {type !== obsTypes.neuro && (
                        <>
                            <Grid item xs={12}>
                                <div
                                    className={clsx(classes.totalScoreRoot, classes.totalScoreRootBorder)}>
                                    <div className={classes.totalScoreLabel}>
                                        {t('Next Obs')}
                                    </div>

                                    <div className={classes.nextObsContent}>
                                        <Grid container spacing={1} alignItems={'center'}>
                                            {/*<Grid item xs={'auto'}>*/}
                                            <Grid item xs={6}>
                                                <AgyleDictionaryField
                                                    className={classes.nextObsCombo}
                                                    options={obsNextItems}
                                                    valueField="value"
                                                    value={nextObs}
                                                    label={t('Next Observation In')}
                                                    noBorder={false}
                                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                    getOptionLabel={(option) => option.description}
                                                    // disabled={!props.allAccepted}
                                                    onChange={(_, value) => updateNextObs(value)}
                                                />
                                            </Grid>

                                            <Grid item>
                                                <div>hrs</div>
                                            </Grid>

                                            <Grid item className={classes.nextObsValue}>
                                                {nextObsDate &&
                                                    <Tooltip
                                                        title={nextObsDate.format(config.defaultDateTimeFormat)}
                                                    >
                                                        <div>
                                                            {nextObsDate.format(config.defaultTimeFormat)}
                                                        </div>
                                                    </Tooltip>
                                                }
                                            </Grid>
                                        </Grid>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} className={classes.clinicalResponseItem}>
                                <InformationBox type="info">
                                    <div className={classes.clinicalResponseContent}>
                                        {t(clinicalResponse?.__description || ``)}
                                    </div>
                                </InformationBox>
                                {/*<div>*/}
                                {/*    <div*/}
                                {/*        className={clsx([*/}
                                {/*            classes.clinicalResponseHeader,*/}
                                {/*            getLabelColor(),*/}
                                {/*        ])}*/}
                                {/*    >*/}
                                {/*        {t('Clinical response')}*/}
                                {/*    </div>*/}

                                {/*    <div className={classes.clinicalResponseContent}>*/}
                                {/*        {clinicalResponse?.__description || ''}*/}
                                {/*    </div>*/}
                                {/*</div>*/}
                            </Grid>

                            {/*<Grid item xs={12} sx={{mb: 1}}>*/}
                            <Grid item xs={12}>
                                {/* AGL-1146 - 006. Sepsis Escalation PEWS */}
                                {/* AGL-1138 - 005. Sepsis Escalation NEWS2 */}
                                <Grid container alignItems="center">
                                    {/*<Grid item sm={2} sx={{display: {xs: 'none', sm: 'block'}}}>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'end'}*/}
                                    {/*        disabled={props.extraOption1Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption1(true)}*/}
                                    {/*                checked={option1 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={3}>*/}
                                    <Grid item style={{display: 'flex', flexBasis: 'content'}} sx={{mr: 2}}>
                                        <AgyleBlockSwitch
                                            onChange={(value) => setOption1(value)}
                                            value={getSwitchVal(option1)}
                                            disabled={props.extraOption1Disabled || !props.allAccepted}
                                            customLabels={{
                                                yes: t('YES'),
                                                no: t('NO'),
                                            }}
                                            valueObject={{
                                                true: true,
                                                false: false,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={8}>
                                        <div className={clsx([classes.optionLabel, classes.optionLabelShort])}>
                                            {t(getOption1Label())}
                                        </div>
                                    </Grid>


                                    {/*<Grid item xs={6} sx={{display: {xs: 'block', sm: 'none'}}}>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption1Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption1(true)}*/}
                                    {/*                checked={option1 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={6} sm={2}>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckRight}*/}
                                    {/*        label={t('NO')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption1Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => {*/}
                                    {/*                    setOption1(false);*/}
                                    {/*                    setOption1Reason(null);*/}
                                    {/*                }}*/}
                                    {/*                checked={option1 === false}*/}
                                    {/*                color={'success'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Grid>

                            {(option1 && newRiskAssessment && type === obsTypes.pews) &&
                                <Grid item xs={12} className={classes.consciousnessLevelChips}>
                                    {nursingOption.map(opt => (
                                        <Tooltip key={opt.id} title={opt.tooltip}>
                                            <Chip
                                                className={classes[opt.color]}
                                                style={{
                                                    margin: '0 10px',
                                                    filter: `brightness(${option1Reason?.id === opt.id ? '100' : '50'}%)`,
                                                }}
                                                label={t(_.startCase(opt.riskLevel))}
                                                onClick={handleSelectOption.bind(this, '1', opt)}
                                                // disabled={!opt.availableAt.includes(props.riskLevel)}
                                            />
                                        </Tooltip>
                                    ))}
                                </Grid>
                            }

                            {(newRiskAssessment && type === obsTypes.pews) &&
                                <>
                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Grid container alignItems="center">
                                            {/*<Grid item sm={2} sx={{display: {xs: 'none', sm: 'block'}}}>*/}
                                            {/*    <FormControlLabel*/}
                                            {/*        className={classes.optionCheckLeft}*/}
                                            {/*        label={t('YES')}*/}
                                            {/*        labelPlacement="end"*/}
                                            {/*        disabled={props.extraOption4Disabled || !props.allAccepted}*/}
                                            {/*        control={*/}
                                            {/*            <Checkbox*/}
                                            {/*                onChange={() => setOption4(true)}*/}
                                            {/*                checked={option4 === true}*/}
                                            {/*                color="error"*/}
                                            {/*            />*/}
                                            {/*        }*/}
                                            {/*    />*/}
                                            {/*</Grid>*/}

                                            <Grid item style={{display: 'flex', flexBasis: 'content'}} sx={{mr: 2}}>
                                                <AgyleBlockSwitch
                                                    onChange={(value) => setOption4(value)}
                                                    value={getSwitchVal(option4)}
                                                    disabled={props.extraOption4Disabled || !props.allAccepted}
                                                    customLabels={{
                                                        yes: t('YES'),
                                                        no: t('NO'),
                                                    }}
                                                    valueObject={{
                                                        true: true,
                                                        false: false,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={8}>
                                                <div className={clsx([classes.optionLabel, classes.optionLabelShort])}>
                                                    {t('Family Concern: Parents are concerned')}
                                                </div>
                                            </Grid>

                                            {/*<Grid item xs={4}>*/}


                                            {/*<Grid item xs={6} sx={{display: {xs: 'block', sm: 'none'}}}>*/}
                                            {/*    <FormControlLabel*/}
                                            {/*        className={classes.optionCheckLeft}*/}
                                            {/*        label={t('YES')}*/}
                                            {/*        labelPlacement="start"*/}
                                            {/*        disabled={props.extraOption4Disabled || !props.allAccepted}*/}
                                            {/*        control={*/}
                                            {/*            <Checkbox*/}
                                            {/*                onChange={() => setOption4(true)}*/}
                                            {/*                checked={option4 === true}*/}
                                            {/*                color="error"*/}
                                            {/*            />}*/}
                                            {/*    />*/}
                                            {/*</Grid>*/}

                                            {/*<Grid item xs={6} sm={2}>*/}
                                            {/*    <FormControlLabel*/}
                                            {/*        className={classes.optionCheckRight}*/}
                                            {/*        label={t('NO')}*/}
                                            {/*        labelPlacement="start"*/}
                                            {/*        disabled={props.extraOption4Disabled || !props.allAccepted}*/}
                                            {/*        control={*/}
                                            {/*            <Checkbox*/}
                                            {/*                onChange={() => {*/}
                                            {/*                    setOption4(false);*/}
                                            {/*                    setOption4Reason(null);*/}
                                            {/*                }}*/}
                                            {/*                checked={option4 === false}*/}
                                            {/*                color="success"*/}
                                            {/*            />*/}
                                            {/*        }*/}
                                            {/*    />*/}
                                            {/*</Grid>*/}
                                        </Grid>
                                    </Grid>

                                    {option4 &&
                                        <Grid item xs={12} className={classes.consciousnessLevelChips}>
                                            {familyOption.map(opt => (
                                                <Tooltip key={opt.id} title={opt.tooltip}>
                                                    <Chip
                                                        className={classes[opt.color]}
                                                        style={{
                                                            margin: '0 10px',
                                                            filter: `brightness(${option4Reason?.id === opt.id ? '100' : '50'}%)`,
                                                        }}
                                                        label={t(_.startCase(opt.riskLevel))}
                                                        onClick={handleSelectOption.bind(this, '4', opt)}
                                                        // disabled={!opt.availableAt.includes(props.riskLevel)}

                                                    />
                                                </Tooltip>
                                            ))}
                                        </Grid>
                                    }
                                </>
                            }

                            <Grid item xs={12} sx={{mt: 1}}>
                                <Grid container alignItems="center">
                                    {/*<Grid*/}
                                    {/*    item*/}
                                    {/*    xs={2}*/}
                                    {/*    sx={{display: {xs: 'none', sm: 'block'}}}*/}
                                    {/*>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'end'}*/}
                                    {/*        disabled={props.extraOption2Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption2(true)}*/}
                                    {/*                checked={option2 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={4} sm={3} md={2}>*/}
                                    <Grid item style={{display: 'flex', flexBasis: 'content'}} sx={{mr: 2}}>
                                        <AgyleBlockSwitch
                                            onChange={(value) => setOption2(value)}
                                            value={getSwitchVal(option2)}
                                            disabled={props.extraOption2Disabled || !props.allAccepted}
                                            customLabels={{
                                                yes: t('YES'),
                                                no: t('NO'),
                                            }}
                                            valueObject={{
                                                true: true,
                                                false: false,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={8}>
                                        <div className={clsx([classes.optionLabel, classes.optionLabelShort])}>
                                            {getAvpuLabel()}
                                        </div>
                                    </Grid>


                                    {/*<Grid*/}
                                    {/*    item*/}
                                    {/*    xs={6}*/}
                                    {/*    sx={{display: {xs: 'block', sm: 'none'}}}*/}
                                    {/*>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption2Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption2(true)}*/}
                                    {/*                checked={option2 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={6} sm={2}>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckRight}*/}
                                    {/*        label={t('NO')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption2Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption2(false)}*/}
                                    {/*                checked={option2 === false}*/}
                                    {/*                color={'success'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{mt: 1}}>
                                <Grid container alignItems="center">
                                    {/*<Grid*/}
                                    {/*    item*/}
                                    {/*    xs={2}*/}
                                    {/*    sx={{display: {xs: 'none', sm: 'block'}}}*/}
                                    {/*>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'end'}*/}
                                    {/*        disabled={props.extraOption3Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption3(true)}*/}
                                    {/*                checked={option3 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={4} sm={3} md={2}>*/}
                                    <Grid item style={{display: 'flex', flexBasis: 'content'}} sx={{mr: 2}}>
                                        <AgyleBlockSwitch
                                            onChange={(value) => setOption3(value)}
                                            value={getSwitchVal(option3)}
                                            disabled={props.extraOption3Disabled || !props.allAccepted}
                                            customLabels={{
                                                yes: t('YES'),
                                                no: t('NO'),
                                            }}
                                            valueObject={{
                                                true: true,
                                                false: false,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={8}>
                                        <div className={clsx([classes.optionLabel, classes.optionLabelShort])}>
                                            {t('USE CLINICAL JUDGEMENT - IS SEPSIS SUSPECTED?')}
                                        </div>
                                    </Grid>


                                    {/*<Grid*/}
                                    {/*    item*/}
                                    {/*    xs={6}*/}
                                    {/*    sx={{display: {xs: 'block', sm: 'none'}}}*/}
                                    {/*>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckLeft}*/}
                                    {/*        label={t('YES')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption3Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption3(true)}*/}
                                    {/*                checked={option3 === true}*/}
                                    {/*                color={'error'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/*<Grid item xs={6} sm={2}>*/}
                                    {/*    <FormControlLabel*/}
                                    {/*        className={classes.optionCheckRight}*/}
                                    {/*        label={t('NO')}*/}
                                    {/*        labelPlacement={'start'}*/}
                                    {/*        disabled={props.extraOption3Disabled || !props.allAccepted}*/}
                                    {/*        control={*/}
                                    {/*            <Checkbox*/}
                                    {/*                onChange={() => setOption3(false)}*/}
                                    {/*                checked={option3 === false}*/}
                                    {/*                color={'success'}*/}
                                    {/*            />*/}
                                    {/*        }*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Grid>

                            {props.extraOption3Disabled && incompleteSepsisPath &&
                                <InformationBox type={'warning'}>
                                    {t('This patient is currently on the Sepsis Management Pathway. Last started on ')}
                                    {moment(incompleteSepsisPath?.dateCreate).format(config.defaultDateTimeFormat)}
                                    {t(' by ')}
                                    {incompleteSepsisPath?.userCreate}
                                </InformationBox>
                            }

                            {!dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_YN_SEPSIS)) && props.extraOption3Disabled &&
                                <InformationBox type={'warning'}>
                                    {t('Your system role privileges prevent you from placing this patient on the Sepsis pathway. Please urgently seek a decision from an appropriate clinical decision maker')}
                                </InformationBox>
                            }

                            {option3ReasonVisible() &&
                                <Grid item xs={12} sx={{mt: 2}}>
                                    <EditorMarkdownComponent
                                        label={t('Enter reason here')}
                                        code={option3Reason}
                                        onChange={(event) => setOption3Reason(event.target.value)}
                                        height={150}
                                        disabled={!props.allAccepted}
                                        hidePreview
                                    />
                                </Grid>
                            }
                        </>
                    )}

                    {type === obsTypes.neuro && config.neuroNextObsTimeAvailable &&
                        <Grid item xs={12}>
                            <div className={clsx(classes.totalScoreRoot, classes.totalScoreRootBorder)}>
                                <div className={classes.totalScoreLabel}>
                                    {t('Next Obs')}
                                </div>
                                <div className={classes.nextObsContent}>
                                    <Grid container spacing={1} alignItems={'center'}>
                                        <Grid item xs={6}>
                                            <AgyleDictionaryField
                                                className={classes.nextObsCombo}
                                                options={obsNeuroNextItems}
                                                valueField="value"
                                                value={nextObs}
                                                label={t('Next Observation In')}
                                                noBorder={false}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                getOptionLabel={(option) => option.description}
                                                // disabled={!props.allAccepted}
                                                onChange={(_, value) => updateNextObs(value)}
                                            />
                                            {/*<ComboBox*/}
                                            {/*    className={classes.nextObsCombo}*/}
                                            {/*    options={obsNeuroNextItems}*/}
                                            {/*    valueField="value"*/}
                                            {/*    value={nextObs}*/}
                                            {/*    placeholder=""*/}
                                            {/*    disabled={!props.allAccepted}*/}
                                            {/*    onChange={updateNextObs}*/}
                                            {/*/>*/}
                                        </Grid>

                                        <Grid item>
                                            <div>hrs</div>
                                        </Grid>

                                        <Grid item className={classes.nextObsValue}>
                                            {nextObsDate &&
                                                <Tooltip title={nextObsDate.format(config.defaultDateTimeFormat)}>
                                                    <div>
                                                        {nextObsDate.format(config.defaultTimeFormat)}
                                                    </div>
                                                </Tooltip>
                                            }
                                        </Grid>
                                    </Grid>
                                </div>
                            </div>
                        </Grid>
                    }
                </Grid>
            </div>
        </div>
    );
}

ObsScore.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    totalScore: PropTypes.number.isRequired,
    prevTotalScore: PropTypes.number,
    nextObsDate: PropTypes.object,
    prevObsDate: PropTypes.object,
    nextObs: PropTypes.object,
    allAccepted: PropTypes.bool.isRequired,
    anyAccepted: PropTypes.bool.isRequired,
    riskLevel: PropTypes.string,
    prevRiskLevel: PropTypes.string,
    extraOption1: PropTypes.oneOf([null, true, false]),
    extraOption2: PropTypes.oneOf([null, true, false]),
    extraOption3: PropTypes.oneOf([null, true, false]),
    extraOption4: PropTypes.oneOf([null, true, false]),
    extraOption1Disabled: PropTypes.bool,
    extraOption2Disabled: PropTypes.bool,
    extraOption3Disabled: PropTypes.bool,
    extraOption4Disabled: PropTypes.bool,
    extraOption1Reason: PropTypes.object,
    extraOption3Reason: PropTypes.string,
    extraOption4Reason: PropTypes.object,
    startedDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    patient: PropTypes.object,
};

ObsScore.defaultProps = {
    type: obsTypes.news,
    nextObs: null,
    extraOption1Disabled: false,
    extraOption2Disabled: false,
    extraOption3Disabled: false,
    extraOption4Disabled: false,
};

export default ObsScore;